// SEO landing page for an individual explore category.
//
// Today the in-app catalog (/explore/categories) routes a category click to
// the legacy `/explore?category=<slug>` query-string filter. That keeps the
// authenticated experience working but gives crawlers a single URL with
// nothing category-specific in the prerendered HTML. This route fixes that:
//
//   /explore/category/<slug>
//
// is statically pre-rendered for every enabled category, with its own
// title/description/canonical/JSON-LD, and re-uses the existing
// `ExplorePageScreen` (filtered by category) so the in-app behaviour is
// identical. The query-string form keeps working unchanged.
//
// Mirrors the SEO pattern used by /tools/[slug], /blog/[slug], and
// /tasks/template/[slug].
import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { exploreApiSsr } from '@/lib/exploreApi';
import ExplorePageScreen from '@/app/(app)/explore/index';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
// Module-level cache populated by `generateStaticParams` so the build-time
// HTML render has access to the same data the runtime render will. Keyed by
// slug.
const _staticCategoryCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// MetaHead helpers
// ---------------------------------------------------------------------------
const SITE_URL = 'https://gab.ai';

const buildCategoryTitle = (cat) => {
  if (cat?.meta?.title) return cat.meta.title;
  if (cat?.name) return `${cat.name} — AI Explore`;
  return 'AI Explore Category';
};

const buildCategoryDescription = (cat) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  if (cat?.name) {
    return `Browse AI-generated ${cat.name.toLowerCase()} on Gab AI. Discover prompts, remix your favorites, and create your own.`;
  }
  return 'Browse a curated feed of AI-generated images, videos, music, and 3D creations on Gab AI.';
};

const buildKeywords = (cat) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const base = ['AI explore', 'AI gallery', 'AI prompts', 'generative AI'];
  if (cat?.name) base.unshift(cat.name, `AI ${cat.name}`);
  if (Array.isArray(cat?.mediaTypes)) {
    for (const mt of cat.mediaTypes) base.push(`AI ${mt}`);
  }
  return base.join(', ');
};

const buildOgImage = (cat) => {
  const item = cat?.featuredItem;
  if (!item) return null;
  return item.cdnPath || item.thumbnailPath || null;
};

const buildStructuredData = (cat, slug) => {
  const url = `${SITE_URL}/explore/category/${slug}`;
  const ogImage = buildOgImage(cat);
  const collection = {
    '@type': 'CollectionPage',
    name: buildCategoryTitle(cat),
    description: buildCategoryDescription(cat),
    url,
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    inLanguage: 'en-US',
    ...(ogImage ? { image: ogImage } : {}),
    ...(cat?.stats?.itemCount
      ? {
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: cat.stats.itemCount,
          },
        }
      : {}),
  };

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Explore', item: `${SITE_URL}/explore` },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Categories',
        item: `${SITE_URL}/explore/categories`,
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: cat?.name || slug,
        item: url,
      },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [collection, breadcrumbs] };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ExploreCategoryRoute() {
  const params = useLocalSearchParams();
  const slug = typeof params?.slug === 'string' ? params.slug : Array.isArray(params?.slug) ? params.slug[0] : null;

  const category = useMemo(() => {
    if (!slug) return null;
    return _staticCategoryCache[slug] || null;
  }, [slug]);

  const seo = useMemo(() => {
    if (!slug) {
      return {
        title: 'Explore Category',
        description: 'Browse AI-generated content on Gab AI.',
        canonical: '/explore/categories',
        keywords: 'AI explore, AI gallery, AI prompts, generative AI',
        structuredData: null,
        image: null,
      };
    }
    return {
      title: buildCategoryTitle(category),
      description: buildCategoryDescription(category),
      canonical: `/explore/category/${slug}`,
      keywords: buildKeywords(category),
      structuredData: buildStructuredData(category, slug),
      image: buildOgImage(category),
    };
  }, [category, slug]);

  return (
    <>
      <MetaHead
        noindex
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        image={seo.image}
        structuredData={seo.structuredData}
      />
      <ExplorePageScreen noindex categorySlug={slug || undefined} />
    </>
  );
}
